import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useKnowledgePanel } from '../../api/hooks';
import type { NetworkEdge, NetworkNodeKind } from '@ni/shared';
import { Claims } from '../brief/claims';
import { subjectStatusLabel } from '../../lib/copy';
import { BOARD_WORDS } from '@ni/shared';
import { formatDate, formatLoose, joinClauses } from '../../lib/format';
import {
  SENSITIVITY_FLOOR,
  type GraphLink,
  type GraphModel,
  type GraphNode,
} from '../../lib/network-graph';
import { overlapOf, overlapText, type Window } from '../../lib/network-overlap';
import { EmptyLine } from '../ui/empty-line';
import { StatusDot } from '../ui/status-word';
import './network.css';

/**
 * **What a line is, and when it held** — the panel a click on the graph opens.
 *
 * The drawing answers *who*. Until now it answered nothing else: an edge said
 * its word in a tooltip and stopped there, so "a board seat, yes, but which
 * years" had no surface at all. Worse, the picture invited a reading it had not
 * earned — two lines into one org look like a connection between the people at
 * their far ends, and a two-year stint in the 2010s is not a connection to
 * someone who played there in the nineties.
 *
 * So this panel does two jobs. It **lists the facts** behind whatever was
 * clicked — the line's own facts for a line, and every incident line's facts
 * grouped by counterpart for a node — each with the capacity it was held in,
 * the years it ran, whatever short phrase the payload had, and a way through to
 * the stored sources. And on an org it **interrogates the time**: for every
 * other person on that node, whether the record holds a stretch when they and
 * the subject were both there. `network-overlap.ts` is the arithmetic
 * and states the rules; the sentence it produces is the answer to Lew's
 * complaint, including the one it must never round off — "Dates not on record"
 * is a gap in the sources, not a finding about the people.
 *
 * Everything rendered here comes off the edge the graph was already holding
 * (`GraphLink.edges`), so opening the panel costs no read. The one thing that
 * does cost a read is the sources drill-down, and it is not fetched until it is
 * asked for. That is what makes the panel work for a **closure** edge — a tie
 * between two of the subject's neighbours, which appears in no subject read and
 * whose record the reader may not be entitled to open. `Claims` is org-scoped
 * rather than subject-scoped, so the citation still resolves.
 *
 * Deliberately *not* here: corroboration counts, pending adjudications,
 * negation history. A reader who wants the row wants the record.
 */

export type NetworkSelection = { kind: 'node'; id: string } | { kind: 'link'; id: string };

/** The kind, in the word the header hangs after a name. */
export const KIND_NOUN: Record<NetworkNodeKind, string> = {
  person: 'person',
  org: 'organization',
  event: 'event',
};

/** A node with no name left is still a node; the graph labels it the same way. */
export const nodeName = (node: GraphNode): string => node.name ?? 'Deleted';

/** Organization tracking is not in the graph payload. Resolve only names in
 * the selected heading through the existing org-scoped knowledge read. */
function OrganisationName({ node }: { node: GraphNode }) {
  const panel = useKnowledgePanel(node.id);
  return panel.data?.entry.tracked && panel.data.entry.kind === 'org' ? (
    <Link className="network-record-link" to={`/organizations/${panel.data.entry.entity_id}`}>
      {nodeName(node)}
    </Link>
  ) : (
    <>{nodeName(node)}</>
  );
}

export function TrackedNodeName({ node }: { node: GraphNode }) {
  if (node.unresolved)
    return (
      <>
        {nodeName(node)} <span className="src">(source mention)</span>
      </>
    );
  if (node.name === null) return <>{nodeName(node)}</>;
  if (node.kind === 'org') return <OrganisationName node={node} />;
  if (node.kind === 'person' && node.status !== null) {
    return (
      <Link className="network-record-link" to={`/people/${node.id}`}>
        {nodeName(node)}
      </Link>
    );
  }
  return <>{nodeName(node)}</>;
}

/* ————————————————————————— the reading ————————————————————————— */

/** "1996 to 2003" / "since 2003" / "until 2003" — a window, said once. */
export function edgeWindowClause(window: NetworkEdge['window']): string | null {
  const start = window?.start_date ?? null;
  const end = window?.end_date ?? null;
  if (start && end) return `${formatLoose(start)} to ${formatLoose(end)}`;
  if (start) return `since ${formatLoose(start)}`;
  if (end) return `until ${formatLoose(end)}`;
  return null;
}

/**
 * The capacity, in words rather than in the catalog's own token.
 *
 * `board_seat.role` is an enum — `advisory`, `trustee` — and printing it raw
 * puts a field value in front of a reader in a place every other surface in the
 * app spells out ("Advisory board member" on the record row). The same table is
 * used here, lowercased into the middle of a clause. A value the table does not
 * hold is passed through: the other two role-bearing types (`employment`,
 * `event_participation`) carry free text, and so might a payload the catalog
 * has since widened.
 */
export const edgeRoleWord = (edge: NetworkEdge): string | null => {
  if (edge.role === null) return null;
  if (edge.type !== 'board_seat') return edge.role;
  return BOARD_WORDS[edge.role]?.toLowerCase() ?? edge.role;
};

/**
 * The fact as one sentence: what it is, in what capacity, over what years.
 *
 * `board_seat` is the one type whose role word *replaces* its label rather than
 * following it. "board seat · board chair" says the same noun twice, and the
 * second saying is the specific one — a chair is a board seat, and a reader who
 * has been told the capacity has already been told the type. Every other type's
 * role is a different word from its label ("employment · principal"), so there
 * the pair earns its place. A seat with no role keeps the label, because then
 * "board seat" is the only thing there is to say.
 */
export const edgeStatement = (edge: NetworkEdge): string => {
  const role = edgeRoleWord(edge);
  const label = edge.type === 'board_seat' && role !== null ? null : edge.label;
  return joinClauses([label, role, edgeWindowClause(edge.window)]);
};

/**
 * The quiet line under the statement. Corroboration first because it is the
 * one a reader acts on; the two stamps after it, and only when they are not the
 * neutral value — a standard, non-severe fact says nothing, which is how every
 * other row in the app reads.
 */
export const edgeStatus = (edge: NetworkEdge): string =>
  joinClauses([
    edge.last_corroborated ? `last corroborated ${formatDate(edge.last_corroborated)}` : null,
    edge.sensitivity !== SENSITIVITY_FLOOR ? 'sensitive' : null,
    edge.severity === 'severe' ? 'severe' : null,
  ]);

/* ————————————————————————— one fact ————————————————————————— */

function Fact({ edge }: { edge: NetworkEdge }) {
  const [showClaims, setShowClaims] = useState(false);
  const status = edgeStatus(edge);

  return (
    <li className="network-fact">
      <p className="network-fact-line">{edgeStatement(edge)}</p>
      {edge.note ? <p className="network-fact-note">{edge.note}</p> : null}
      <p className="network-fact-source">
        <button
          type="button"
          className="src-toggle disclose"
          aria-expanded={showClaims}
          onClick={() => setShowClaims((open) => !open)}
        >
          {status.length > 0 ? status : 'sources'}
        </button>
      </p>
      {/* Mounted, not hidden: the claims read is a request, and a panel that
          fetched every fact's sources on open would spend a dozen of them to
          answer a question nobody asked. */}
      {showClaims ? <Claims lineageId={edge.lineage_id} /> : null}
    </li>
  );
}

/* ————————————————————————— time overlap ————————————————————————— */

/** The windows on every fact tying two entities together, undated ones included. */
function windowsBetween(model: GraphModel, one: string, two: string): Window[] {
  const link = model.links.find(
    (entry) =>
      (entry.source === one && entry.target === two) ||
      (entry.source === two && entry.target === one),
  );
  return link ? link.edges.map((edge) => edge.window) : [];
}

/**
 * Everyone else standing on this node, and whether the record puts them there
 * at the same time as the subject.
 *
 * Only people, and only on an org: "was Ellen there when he was" is a question
 * about a place two people passed through. Person-to-person the question is
 * already answered by the line between them, and the subject's own node has no
 * counterpart to ask it of.
 *
 * An **event** is left out for a different reason: `event_participation` has no
 * window (network-edge-detail.ts), so every row under an event would read
 * "Dates not on record" — a section that can only ever say it knows nothing is
 * worse than no section, because it reads as a finding about the people rather
 * than as the shape of the type. The node's name carries the edition, which is
 * the answer to "when" an event has.
 */
export interface OverlapRow {
  id: string;
  name: string;
  text: string;
}

export function overlapRows(model: GraphModel, subjectId: string, nodeId: string): OverlapRow[] {
  const node = model.nodes.find((entry) => entry.id === nodeId);
  if (!node || node.id === subjectId) return [];
  if (node.kind !== 'org') return [];

  const mine = windowsBetween(model, subjectId, nodeId);
  const rows: OverlapRow[] = [];

  for (const other of model.nodes) {
    if (other.kind !== 'person' || other.id === subjectId || other.id === nodeId) continue;
    const theirs = windowsBetween(model, other.id, nodeId);
    // Not on this node at all — the graph draws no line, so there is nothing
    // to ask about.
    if (theirs.length === 0) continue;
    rows.push({
      id: other.id,
      name: nodeName(other),
      text: overlapText(overlapOf(mine, theirs)),
    });
  }

  return rows;
}

/** "Daniel" from "Daniel Voss" — the section head says who the reader is asking about. */
const firstName = (name: string): string => name.split(' ')[0] || name;

/* ————————————————————————— the panel ————————————————————————— */

export interface Group {
  key: string;
  /** The counterpart's name, or null when the panel is showing a single line. */
  head: string | null;
  /** The counterpart, so its head can open its own panel; null on a line. */
  nodeId: string | null;
  edges: NetworkEdge[];
}

/**
 * What the selection resolves to: a title, the facts under it, and the node.
 *
 * `sub` reads "person · Member" where the org holds a subject record for the
 * person — the kind noun the header has always hung after a name, and then what
 * that person is to the org, in the org's own word. It stays the bare noun for
 * everyone else, and for a status this build carries no word for: the kind is
 * always sayable, the vocabulary is not (`copy.ts`).
 */
export function readSelection(
  model: GraphModel,
  selection: NetworkSelection,
  subjectId: string,
  vertical: string,
): {
  title: string;
  titleNodes: GraphNode[];
  sub: string | null;
  groups: Group[];
  nodeId: string | null;
} | null {
  const byId = new Map(model.nodes.map((node) => [node.id, node]));

  if (selection.kind === 'link') {
    const link = model.links.find((entry) => entry.id === selection.id);
    if (!link) return null;
    // Endpoints are stored in id order, which is nothing to a reader. The
    // subject is the page, so it is named first where it is one of the two; a
    // closure edge keeps the model's order, having no subject to lead with.
    const [first, second] =
      link.target === subjectId ? [link.target, link.source] : [link.source, link.target];
    const source = byId.get(first);
    const target = byId.get(second);
    if (!source || !target) return null;
    return {
      title: `${nodeName(source)} and ${nodeName(target)}`,
      titleNodes: [source, target],
      sub: null,
      groups: [{ key: link.id, head: null, nodeId: null, edges: link.edges }],
      nodeId: null,
    };
  }

  const node = byId.get(selection.id);
  if (!node) return null;

  // Grouped in the model's own node order (kind, then name), which is the order
  // the ring was laid out in — so the panel reads down in the order the eye
  // travels round the picture.
  const incident = new Map<string, GraphLink>();
  for (const link of model.links) {
    if (link.source === node.id) incident.set(link.target, link);
    else if (link.target === node.id) incident.set(link.source, link);
  }

  const groups: Group[] = [];
  for (const other of model.nodes) {
    const link = incident.get(other.id);
    if (!link) continue;
    groups.push({ key: link.id, head: nodeName(other), nodeId: other.id, edges: link.edges });
  }

  const status = node.status === null ? null : subjectStatusLabel(vertical, node.status);

  return {
    title: nodeName(node),
    titleNodes: [node],
    sub: status === null ? KIND_NOUN[node.kind] : `${KIND_NOUN[node.kind]} · ${status}`,
    groups,
    nodeId: node.id,
  };
}

export function NetworkDetail({
  model,
  selection,
  subjectId,
  vertical,
  onClose,
  onSelectNode,
  handlesEscape = true,
}: {
  model: GraphModel;
  selection: NetworkSelection;
  subjectId: string;
  /** `org.vertical` — whose words the status line is said in. */
  vertical: string;
  onClose: () => void;
  /** Open a counterpart's own panel from its head. Unset, the heads are plain. */
  onSelectNode?: (id: string) => void;
  /**
   * Whether this panel binds Escape itself. False inside `Modal`, which already
   * binds it on the same window — two listeners on one key is one close too
   * many the moment anything upstream counts them.
   */
  handlesEscape?: boolean;
}) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const read = useMemo(
    () => readSelection(model, selection, subjectId, vertical),
    [model, selection, subjectId, vertical],
  );
  const rows = useMemo(
    () => (read?.nodeId ? overlapRows(model, subjectId, read.nodeId) : []),
    [model, subjectId, read?.nodeId],
  );

  /* Escape closes, the way every modal in the app does — and it has to be the
     window rather than the panel, because the reader's focus may well be back
     on the graph: the panel is not a modal, and hopping node to node with it
     open is the whole point of it not being one. */
  useEffect(() => {
    if (!handlesEscape) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handlesEscape, onClose]);

  /* Focus lands on the close button when the panel *opens* — the one control
     that is on it whatever was clicked, and the one a reader who arrived by
     keyboard needs first. On mount and only on mount: the graph unmounts this
     while nothing is selected, so a mount is exactly a closed-to-open, and
     hopping node to node with the panel already open must leave focus where the
     reader put it rather than yanking it back here on every click. Where focus
     goes when it closes is the graph's business: the tab puts it back on the
     mark that opened this. */
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  const subject = model.nodes.find((node) => node.id === subjectId);

  if (!read) return null;

  /* The status dot beside the kind line, for the one node a panel about a node
     is about. A line's head names two nodes and so carries none. */
  const only = read.titleNodes.length === 1 ? read.titleNodes[0] : undefined;

  return (
    <div className="network-detail" role="group" aria-label={read.title}>
      <div className="network-detail-head">
        <div className="network-detail-heading">
          <h2 className="network-detail-title">
            {read.titleNodes.map((node, index) => (
              <Fragment key={node.id}>
                {index > 0 ? <span className="network-detail-and"> and </span> : null}
                <TrackedNodeName node={node} />
              </Fragment>
            ))}
          </h2>
          {read.sub ? (
            <p className="network-detail-kind">
              {only?.status != null && subjectStatusLabel(vertical, only.status) !== null ? (
                <StatusDot vertical={vertical} status={only.status} />
              ) : null}
              {read.sub.charAt(0).toUpperCase() + read.sub.slice(1)}
            </p>
          ) : null}
        </div>
        <button
          ref={closeRef}
          type="button"
          className="network-detail-close"
          aria-label="Close"
          onClick={onClose}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M3.5 3.5l7 7M10.5 3.5l-7 7" />
          </svg>
        </button>
      </div>

      <div className="network-detail-body">
        {read.groups.length === 0 ? (
          <EmptyLine>Nothing on record for this one.</EmptyLine>
        ) : (
          read.groups.map((group) => (
            <section className="network-detail-group" key={group.key}>
              {group.head ? (
                <h3 className="network-group-head">
                  {onSelectNode && group.nodeId ? (
                    <button
                      type="button"
                      className="network-group-go"
                      onClick={() => {
                        onSelectNode(group.nodeId!);
                        // The head is about to be replaced by the new panel's
                        // own; focus stays on the panel rather than dropping out.
                        closeRef.current?.focus();
                      }}
                    >
                      {group.head}
                    </button>
                  ) : (
                    group.head
                  )}
                  {group.edges.length > 1 ? (
                    <span className="network-group-count">{group.edges.length}</span>
                  ) : null}
                </h3>
              ) : null}
              <ul className="network-facts">
                {group.edges.map((edge) => (
                  <Fact key={edge.lineage_id} edge={edge} />
                ))}
              </ul>
            </section>
          ))
        )}

        {rows.length > 0 && subject ? (
          <section className="network-overlap">
            <h3 className="network-overlap-head">
              Shared affiliations with {firstName(nodeName(subject))}
            </h3>
            <p className="network-affiliation-hint">
              Shared affiliations do not establish a personal relationship.
            </p>
            <ul>
              {rows.map((row) => (
                <li className="network-overlap-row" key={row.id}>
                  <span className="network-overlap-who">{row.name}</span>
                  <span className="network-overlap-when">{row.text}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </div>
  );
}
